import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui-kit/button';
import { Skeleton } from '@/components/ui-kit/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { cn } from '@/lib/utils';
import { sortOfficeDays } from '../../schemas/work-status';
import { WEEKDAYS } from '../../schemas/working-info-schema';

export interface WfoDaysDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Days already saved for the employee; they start ticked. */
  initialDays?: string[];
  /** Work days of the employee's current HR term; only these can be office days. Empty when none is on
   * file, which leaves every day open. */
  workDays?: string[];
  /** The work days are still loading: no day can be picked yet. */
  isLoadingWorkDays?: boolean;
  isSaving?: boolean;
  onConfirm: (days: string[]) => void;
}

/**
 * "Work from office": which days of the week an employee comes to the office once work from home is
 * off (legacy `WfoDialogComponent`). Days off in the current HR term cannot be picked, and a saved day
 * that is no longer a work day is dropped. At least one day is needed before it can be confirmed.
 */
export const WfoDaysDialog = ({
  open,
  onOpenChange,
  initialDays,
  workDays,
  isLoadingWorkDays = false,
  isSaving = false,
  onConfirm,
}: WfoDaysDialogProps) => {
  const { t } = useTranslation();
  const [days, setDays] = useState<string[]>(() => sortOfficeDays(initialDays));

  // Every opening starts from what is saved for the employee it was opened for.
  useEffect(() => {
    if (open) setDays(sortOfficeDays(initialDays));
  }, [open, initialDays]);

  const isWorkDay = (day: string) => !workDays?.length || workDays.includes(day);
  const pickedDays = sortOfficeDays(days.filter(isWorkDay));

  const toggle = (day: string) =>
    setDays((current) =>
      current.includes(day) ? current.filter((item) => item !== day) : [...current, day]
    );

  return (
    <Dialog open={open} onOpenChange={(next) => !isSaving && onOpenChange(next)}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>{t('WORK_FROM_OFFICE')}</DialogTitle>
          <DialogDescription>{t('SELECT_WORKING_DAYS')}</DialogDescription>
        </DialogHeader>

        <div
          className="grid grid-cols-2 gap-3 sm:grid-cols-4"
          role="group"
          aria-label={t('WORK_FROM_OFFICE')}
        >
          {/* Which days the employee works decides which can be picked: placeholders until known. */}
          {isLoadingWorkDays &&
            WEEKDAYS.map((day) => <Skeleton key={day} className="h-10 rounded-lg" />)}
          {!isLoadingWorkDays &&
            WEEKDAYS.map((day) => {
              const selected = pickedDays.includes(day);
              return (
                <button
                  key={day}
                  type="button"
                  aria-pressed={selected}
                  disabled={!isWorkDay(day)}
                  onClick={() => toggle(day)}
                  className={cn(
                    'rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:border-disabled-border disabled:bg-disabled disabled:text-disabled-foreground',
                    selected
                      ? 'border-primary bg-accent text-primary'
                      : 'border-border bg-card text-foreground enabled:hover:bg-muted'
                  )}
                >
                  {t(day)}
                </button>
              );
            })}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            {t('CANCEL')}
          </Button>
          <Button
            type="button"
            onClick={() => onConfirm(pickedDays)}
            loading={isSaving}
            disabled={isSaving || isLoadingWorkDays || pickedDays.length === 0}
          >
            {t('CONFIRM')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
